<script setup lang="ts">
import { useRouter } from "vue-router";
import { activationGroups } from "@/constants/activations";
import { ActivationGroup } from "@/types/calculators";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { useI18n } from "vue-i18n";
import activationBG from "@/assets/calculators/activations/header_bg.webp";
import GradientButton from "@/components/buttons/GradientButton.vue";

const router = useRouter();

const { t } = useI18n();

function openActivation(group: ActivationGroup) {
  router.push({
    name: ROUTE_NAMES.ACTIVATION_FORM,
    query: {
      activation: group.key,
    },
  });
}
</script>

<template>
  <div class="w-full h-full overflow-y-auto no-scrollbar pb-10">
    <div
      class="relative w-full h-[204px] md:h-[259px] xl:h-[342px] overflow-hidden bg-cover bg-center flex items-end pl-4 pb-[23px] md:pl-6 md:pb-[69px] xl:pl-[73px] xl:pb-[83px] mb-4 md:mb-6 xl:mb-10"
      :style="{
        backgroundImage: `url(${activationBG})`,
      }"
    >
      <div class="absolute inset-0 bg-mono-brown-gradient"></div>
      <p
        class="yeseva-one font-normal text-5xl md:text-[64px] xl:text-8xl leading-100p text-white z-10"
      >
        {{ t("calculators.activations.title") }}
      </p>
    </div>

    <div
      class="activations-grid gap-4 md:gap-x-6 md:gap-y-[14px] xl:gap-8 justify-center px-1 sm:grow"
    >
      <!-- ACTIVATION CARD -->
      <div
        v-for="group in activationGroups"
        :key="group.key"
        class="flex flex-col bg-white rounded-2xl w-full max-md:min-h-[336px] md:h-[432px] xl:h-[530px] cursor-pointer group/gradient card-shadow"
        @click="openActivation(group)"
      >
        <!-- CARD IMAGE -->
        <div
          class="relative w-full h-32 md:h-32 xl:h-[206px] overflow-hidden bg-cover bg-center flex items-end pb-2 md:pb-4 xl:pb-6 justify-center rounded-t-2xl"
          :style="{
            backgroundImage: `url(${require(`@/assets/calculators/activations/${group.key}.jpg`)})`,
          }"
        >
          <div
            class="absolute inset-0 bg-mono-brown-gradient group-hover/gradient:bg-mono-active-gradient"
          ></div>
          <p
            class="font-semibold text-base leading-[21px] xl:text-2xl xl:leading-[32px] text-white z-10"
          >
            {{ t(`calculators.activations.group.${group.key}`) }}
          </p>
        </div>

        <div class="flex flex-col flex-1 pb-4 md:pb-6 xl:pb-8 px-4 xl:px-8">
          <ul
            class="max-md:my-4 pl-7 list-disc text-brown text-sm leading-[21px] md:text-base md:leading-[24px] xl:text-xl xl:leading-[28px] text-left flex-1 flex flex-col justify-center"
          >
            <template
              v-if="group.activationKeys && group.activationKeys.length > 1"
            >
              <li
                v-for="key in group.activationKeys"
                :key="key"
                class="break-words"
              >
                {{ t(`calculators.activations.activation_list.${key}`) }}
              </li>
            </template>
            <template v-else>
              <li class="break-words">
                {{
                  t(`calculators.activations.group_descriptions.${group.key}`)
                }}
              </li>
            </template>
          </ul>

          <div class="mt-auto flex justify-center">
            <GradientButton
              class="w-[153px] h-[46px] md:w-[191px] md:h-14"
              bg-color="bg-brown-gradient"
              hover-bg-color="bg-orange-gradient"
              text="Рассчитать"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.activations-grid {
  @apply grid
  grid-cols-[repeat(auto-fill,calc(100%-16px))]
  sm:grid-cols-[repeat(auto-fill,246px)]
  xl:grid-cols-[repeat(auto-fill,396px)];
}
.card-shadow {
  box-shadow: 0 8px 20px 0 #29160e1a;
}
</style>
